// Constants
import { LIST_TRUNCATE_LIMIT } from '@/constants/chatbot';
import { DATE_FORMATS } from '@shared/constants';

// Utils
import {
  formatTruncatedList,
  formatListWithAnd,
  normalizeString,
} from '@/utils/textUtils';
import { formatDayMonthYearLabel } from '@shared/utils';

// Types
import type { EmployeeCertification } from '@/types/employee';

export interface GroupedCertification {
  org: string;
  entries: EmployeeCertification[];
}

/** Group certifications by organization */
export const groupCertificationsByOrg = (
  certifications: EmployeeCertification[],
): GroupedCertification[] => {
  const grouped = new Map<string, EmployeeCertification[]>();

  for (const cert of certifications) {
    const org = cert.org || 'Unknown Organization';

    // Check if this organization already exists in the map
    const existing = grouped.get(org);

    // If organization exists, add certification to existing array
    if (existing) {
      existing.push(cert);
    }

    // If organization doesn't exist, create new entry with this certification
    else {
      grouped.set(org, [cert]);
    }
  }

  // Transform Map entries into array of GroupedCertification objects
  return Array.from(grouped, ([org, entries]) => ({ org, entries }));
};

export const isCertificationInError = (
  c: EmployeeCertification,
  errorText: string,
): boolean => {
  const normalized = normalizeString(errorText);
  const name = normalizeString(c.name || '');
  return Boolean(name && normalized.includes(name));
};

export const extractCertificationNames = (rawMsg: string): string[] => {
  const matches = [...rawMsg.matchAll(/name:\s*"([^"]+)"/gi)];
  return matches.map((m) => m[1]).filter((s): s is string => Boolean(s));
};

export const buildCertificationSuccessMessage = (
  addSucceeded: EmployeeCertification[],
  removeSucceeded: EmployeeCertification[],
): string => {
  const hasBoth = addSucceeded.length > 0 && removeSucceeded.length > 0;

  const formatAction = (
    items: EmployeeCertification[],
    action: 'added' | 'removed',
    startIndex: number,
  ) => {
    if (!items.length) return '';

    // Filter out items with empty names
    const validItems = items.filter((c) => c.name && c.name.trim());
    if (validItems.length === 0) return '';

    const actionText =
      action === 'added'
        ? 'saved to your certifications'
        : 'removed from your certifications';

    // Single item without mixed operations: no index
    if (validItems.length === 1 && !hasBoth)
      return `${validItems[0]!.name} has been ${actionText}`;

    // Multiple items or mixed operations: use sequential indices
    const names = validItems.map((c, idx) => `(${startIndex + idx}) ${c.name}`);
    const verb = validItems.length === 1 ? 'has' : 'have';

    return `${formatTruncatedList(names, LIST_TRUNCATE_LIMIT, formatListWithAnd)} ${verb} been ${actionText}`;
  };

  if (hasBoth) {
    const addMsg = formatAction(addSucceeded, 'added', 1);
    const removeMsg = formatAction(
      removeSucceeded,
      'removed',
      addSucceeded.length + 1,
    );

    // If both messages are empty, return empty string
    if (!addMsg && !removeMsg) return '';
    // If one is empty, return the other
    if (!addMsg) return removeMsg;
    if (!removeMsg) return addMsg;

    return `${addMsg} and ${removeMsg}`;
  }

  if (addSucceeded.length) return formatAction(addSucceeded, 'added', 1);
  if (removeSucceeded.length)
    return formatAction(removeSucceeded, 'removed', 1);

  return '';
};

/**
 * Builds "CertName (YYYY)" when an issue date is available
 * @param name - Certification name
 * @param issue - Issue date string
 * @returns Formatted header e.g. "AWS SAA (2024)"
 */
export const buildCertHeader = (name: string, issue?: string): string => {
  const yearLabel = issue ? formatDayMonthYearLabel(issue, 'YYYY') : '';

  return yearLabel ? `${name} (${yearLabel})` : name;
};

/**
 * Builds a pipe-separated certification line for resume templates.
 * Empty segments are omitted so partial data renders cleanly.
 * @param name - Certification name
 * @param issue - Issue date string
 * @returns Formatted string e.g. "Cert Name | Mon YYYY"
 */
export const buildCertLine = (name: string, issue?: string): string => {
  const monthYearLabel = issue
    ? formatDayMonthYearLabel(issue, DATE_FORMATS.SHORT_MONTH_YEAR)
    : '';

  return [name, monthYearLabel].filter(Boolean).join(' | ');
};
